import { action, makeObservable, observable, reaction } from 'mobx';
import { userInfoStore } from '../../../store/userInfoStore';
import { crashGameStore } from './gameStore';
import { parseNumberPrecisionByDoteCount } from '../../../core/helpers/parseNumberByPrecision';
import {
  CRASH_RTP,
  CRASH_AUTO_CASH_OUT_MIN_ODD,
  CRASH_DEFAULT_WIN_CHANCE_VALUE,
  CRASH_GAME_TYPE_ID,
  MULTIPLIER_LIMIT,
  MULTIPLIER_PRECISION,
  MULTIPLIER_STEP_VALUE,
  WIN_CHANCE_LIMIT,
  WIN_CHANCE_PRECISION,
  WIN_CHANCE_STEP_VALUE,
} from '../crashCore/constants';
import { autoBetStore } from '../../../store/autoBetStore';
import { freeBetStore } from '../../../store/freeBetStore';

class CrashBetsStore {
  isBetsClosed = false;

  limits = MULTIPLIER_LIMIT;

  cashedOutData = null;

  wonUsersList = [];

  multiplier = CRASH_AUTO_CASH_OUT_MIN_ODD;

  winChance = CRASH_DEFAULT_WIN_CHANCE_VALUE;

  currentWinAmount = null;

  betData = {
    betId: null,
    isNextRoundBet: false,
    isBetAccepted: false,
    winAmount: null,
    finishedOdd: null,
    isDisabled: false,
  };

  isAllowDoBet = true;

  constructor() {
    makeObservable(this, {
      betData: observable.deep,
      isBetsClosed: observable,
      isAllowDoBet: observable,
      winChance: observable,
      limits: observable,
      wonUsersList: observable.deep,
      cashedOutData: observable.deep,
      multiplier: observable,
      resetDoBetData: action.bound,
      currentWinAmount: observable.deep,
      updateIsDisabledBet: action.bound,
      updateBetAccepted: action.bound,
      setIsBetsClosed: action.bound,
      setMultiplier: action.bound,
      resetWonUsersList: action.bound,
      setLimits: action.bound,
      setCashedOutData: action.bound,
      setIsAllowDoBet: action.bound,
      resetCashedOutData: action.bound,
      updateWonUsersList: action.bound,
      setCurrentWinAmount: action.bound,
      handleBetForNextRound: action.bound,
      handleIncreaseWinChance: action.bound,
      handleIncreaseMultiplier: action.bound,
    });

    reaction(
      () => [crashGameStore.roundId, this.isBetsClosed],
      (value, prev) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        if (value && prev && value !== prev) {
          setIsAllowedChangeCurrency(!this.isBetsClosed);
        }
      }
    );

    reaction(
      () => this.betData.isBetAccepted,
      (value) => {
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );

    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prev) => {
        if (value && value.minOdd && (!prev || prev.id !== value.id)) {
          this.setLimits({
            minBet: value.minOdd,
          });
        } else if (!value) {
          this.setLimits(MULTIPLIER_LIMIT);
        }
      }
    );
  }

  setIsAllowDoBet(bool) {
    this.isAllowDoBet = bool;
  }

  handleIncreaseWinChance(isIncrease) {
    const nextWinChanceValue = +this.winChance + (isIncrease ? 1 : -1) * WIN_CHANCE_STEP_VALUE;
    if (nextWinChanceValue > WIN_CHANCE_LIMIT.maxBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.maxBet);
    } else if (nextWinChanceValue < WIN_CHANCE_LIMIT.minBet) {
      this.setWinChance(WIN_CHANCE_LIMIT.minBet);
    } else {
      const newWinChance = parseNumberPrecisionByDoteCount(
        1,
        nextWinChanceValue,
        WIN_CHANCE_PRECISION
      );
      this.setWinChance(Number.isFinite(newWinChance) ? +newWinChance : 0);
    }
  }

  setCurrentWinAmount(amount) {
    this.currentWinAmount = amount;
  }

  setCashedOutData(data) {
    this.cashedOutData = data;

    setTimeout(() => {
      this.resetCashedOutData();
      this.resetWonUsersList();
    }, 2000);
  }

  handleIncreaseMultiplier(isIncrease) {
    const { currentFreeBet } = freeBetStore;
    const nextMultiplierValue = +this.multiplier + (isIncrease ? 1 : -1) * MULTIPLIER_STEP_VALUE;
    if (currentFreeBet && nextMultiplierValue < currentFreeBet.minOdd) {
      this.setMultiplier(currentFreeBet.minOdd);
      return;
    }
    if (nextMultiplierValue > MULTIPLIER_LIMIT.maxBet) {
      this.setMultiplier(MULTIPLIER_LIMIT.maxBet);
    } else if (nextMultiplierValue < MULTIPLIER_LIMIT.minBet) {
      this.setMultiplier(MULTIPLIER_LIMIT.minBet);
    } else {
      const newMultiplier = parseNumberPrecisionByDoteCount(
        1,
        nextMultiplierValue,
        MULTIPLIER_PRECISION
      );
      this.setMultiplier(Number.isFinite(newMultiplier) ? +newMultiplier : 0);
    }
  }

  buildDoBetModel = () => {
    const { roundId } = crashGameStore;
    return {
      roundId: roundId,
      isAutoplay: autoBetStore.isAutoBetOn,
      gameTypeId: CRASH_GAME_TYPE_ID, // TODO change
      betData: {
        autoCashoutOdd: this.multiplier,
      },
    };
  };

  updateWonUsersList(wonUser) {
    this.wonUsersList = [...this.wonUsersList, wonUser];
  }

  resetWonUsersList() {
    this.wonUsersList = [];
  }

  setLimits(limits) {
    this.limits = {
      ...this.limits,
      ...limits,
    };
  }

  resetCashedOutData() {
    this.cashedOutData = null;
  }

  setWinChance = (winChance) => {
    this.winChance = winChance;
    const newMultiplier = parseNumberPrecisionByDoteCount(
      100,
      CRASH_RTP / +winChance,
      MULTIPLIER_PRECISION
    );
    this.multiplier = Number.isFinite(newMultiplier) ? +newMultiplier : 0;
  };

  setMultiplier(multiplier) {
    this.multiplier = multiplier;
    const newWinChance = parseNumberPrecisionByDoteCount(
      100,
      CRASH_RTP / +multiplier,
      WIN_CHANCE_PRECISION
    );
    this.winChance = Number.isFinite(newWinChance) ? +newWinChance : 0;
  }

  updateIsDisabledBet(isDisabled) {
    this.betData = {
      ...this.betData,
      isDisabled: isDisabled,
    };
  }

  resetDoBetData() {
    this.betData = {
      ...this.betData,
      amount: userInfoStore.limits.minBet || 0,
      isNextRoundBet: false,
      isBetAccepted: false,
    };
  }

  resetStore() {
    this.isBetsClosed = false;
    this.cashedOutData = null;
    this.wonUsersList = [];
    this.multiplier = CRASH_AUTO_CASH_OUT_MIN_ODD;
    this.winChance = CRASH_DEFAULT_WIN_CHANCE_VALUE;
    this.currentWinAmount = null;
    this.betData = {
      betId: null,
      isNextRoundBet: false,
      isBetAccepted: false,
      winAmount: null,
      finishedOdd: null,
      isDisabled: false,
    };
    this.isAllowDoBet = true;
  }

  setIsBetsClosed(isBetsClosed) {
    this.isBetsClosed = isBetsClosed;
  }

  handleBetForNextRound(value) {
    this.betData = {
      ...this.betData,
      isNextRoundBet: value !== undefined ? value : !this.betData.isNextRoundBet,
    };
    this.isAllowDoBet = true;
  }

  updateBetAccepted(isBetAccepted, betId) {
    this.betData = {
      ...this.betData,
      isBetAccepted,
      betId,
    };
  }
}

export const crashBetsStore = new CrashBetsStore();
